import { BaseApiClient } from "../api/baseApiClient.service";

export type ShareLinkType = "CHAPTER" | "PODCAST";

export interface ShareableLink {
	id: string;
	type: ShareLinkType;
	chapterId?: string;
	podcastId?: string;
	shareToken: string;
	createdById: string;
	createdAt: string;
	expiresAt: string | null;
	isActive: boolean;
	viewCount: number;
	previewDuration: number; // in milliseconds
	shareUrl?: string;
}

export interface SharedChapterInfo {
	id: string;
	title: string;
	length: number;
	index: number;
	language: string;
	podcastTitle: string;
	podcastId: string;
}

export interface SharedPodcastInfo {
	id: string;
	title: string;
	length: number;
	language: string;
	transcription: string;
	chapters: Array<{
		id: string;
		title: string;
		length: number;
		index: number;
	}>;
}

export interface SharedChapterData {
	chapter: SharedChapterInfo;
	isAuthenticated: boolean;
	previewDuration: number | null;
	shareToken: string;
}

export interface SharedPodcastData {
	podcast: SharedPodcastInfo;
	isAuthenticated: boolean;
	previewDuration: number | null;
	shareToken: string;
}

export interface SharedAudioUrl {
	url: string;
	expiresIn: number;
	fullAccess: boolean;
	previewDuration: number | null;
	chapterLength?: number;
	podcastLength?: number;
}

/**
 * Unified service for managing shareable links (chapters and podcasts)
 */
export class ShareableLinkService {
	static path = "podcast";

	/**
	 * Create a shareable link for a chapter
	 */
	static async createChapterShareLink(
		chapterId: string,
		options?: {
			previewDuration?: number;
			expiresAt?: string;
		},
	): Promise<ShareableLink> {
		return BaseApiClient.getInstance().post<
			ShareableLink,
			{
				previewDuration?: number;
				expiresAt?: string;
			}
		>(`${ShareableLinkService.path}/chapters/${chapterId}/share`, {
			previewDuration: options?.previewDuration,
			expiresAt: options?.expiresAt,
		});
	}

	/**
	 * Create a shareable link for a podcast
	 */
	static async createPodcastShareLink(
		podcastId: string,
		options?: {
			previewDuration?: number;
			expiresAt?: string;
		},
	): Promise<ShareableLink> {
		return BaseApiClient.getInstance().post<
			ShareableLink,
			{
				previewDuration?: number;
				expiresAt?: string;
			}
		>(`${ShareableLinkService.path}/podcasts/${podcastId}/share`, {
			previewDuration: options?.previewDuration,
			expiresAt: options?.expiresAt,
		});
	}

	/**
	 * Get shared chapter info by token (public endpoint)
	 */
	static async getSharedChapter(
		shareToken: string,
	): Promise<SharedChapterData> {
		return BaseApiClient.getInstance().get<SharedChapterData>(
			`${ShareableLinkService.path}/shared/chapter/${shareToken}`,
		);
	}

	/**
	 * Get shared podcast info by token (public endpoint)
	 */
	static async getSharedPodcast(
		shareToken: string,
	): Promise<SharedPodcastData> {
		return BaseApiClient.getInstance().get<SharedPodcastData>(
			`${ShareableLinkService.path}/shared/podcast/${shareToken}`,
		);
	}

	/**
	 * Get audio URL for shared chapter
	 */
	static async getSharedChapterAudioUrl(
		shareToken: string,
	): Promise<SharedAudioUrl> {
		return BaseApiClient.getInstance().get<SharedAudioUrl>(
			`${ShareableLinkService.path}/shared/chapter/${shareToken}/audio`,
		);
	}

	/**
	 * Get audio URL for shared podcast (full joined podcast)
	 */
	static async getSharedPodcastAudioUrl(
		shareToken: string,
	): Promise<SharedAudioUrl> {
		return BaseApiClient.getInstance().get<SharedAudioUrl>(
			`${ShareableLinkService.path}/shared/podcast/${shareToken}/audio`,
		);
	}

	/**
	 * Get audio URL for a specific chapter within a shared podcast
	 */
	static async getSharedPodcastChapterAudioUrl(
		shareToken: string,
		chapterId: string,
	): Promise<SharedAudioUrl> {
		return BaseApiClient.getInstance().get<SharedAudioUrl>(
			`${ShareableLinkService.path}/shared/podcast/${shareToken}/chapter/${chapterId}/audio`,
		);
	}

	/**
	 * Get all share links for a chapter
	 */
	static async getShareLinksForChapter(
		chapterId: string,
	): Promise<ShareableLink[]> {
		const response = await BaseApiClient.getInstance().get<{
			shareLinks: ShareableLink[];
		}>(`${ShareableLinkService.path}/chapters/${chapterId}/share`);
		return response.shareLinks;
	}

	/**
	 * Get all share links for a podcast
	 */
	static async getShareLinksForPodcast(
		podcastId: string,
	): Promise<ShareableLink[]> {
		const response = await BaseApiClient.getInstance().get<{
			shareLinks: ShareableLink[];
		}>(`${ShareableLinkService.path}/podcasts/${podcastId}/share`);
		return response.shareLinks;
	}

	/**
	 * Deactivate a share link (works for both chapter and podcast)
	 */
	static async deactivateShareLink(shareToken: string): Promise<void> {
		await BaseApiClient.getInstance().delete<{ success: boolean }>(
			`${ShareableLinkService.path}/share/${shareToken}`,
		);
	}

	/**
	 * Copy share URL to clipboard
	 */
	static copyShareUrl(shareUrl: string): Promise<void> {
		return navigator.clipboard.writeText(shareUrl);
	}

	/**
	 * Format preview duration for display
	 */
	static formatPreviewDuration(durationMs: number): string {
		const seconds = Math.floor(durationMs / 1000);
		if (seconds < 60) {
			return `${seconds}s`;
		}
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		if (remainingSeconds === 0) {
			return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
		}
		return `${minutes}m ${remainingSeconds}s`;
	}
}

// Keep backward compatibility with existing code
export const ShareableChapterLinkService = {
	...ShareableLinkService,
	createShareLink: ShareableLinkService.createChapterShareLink,
};
